import './Widget.css';

import React, { useEffect, useState } from 'react';
import MoreButton from '../../../b2b-common/MoreButton';
import { Delete, Edit, MoreVert } from '@mui/icons-material';
import WidgetFactory from './WidgetFactory';
import { debounce, isEqual } from 'lodash';
import ConfirmationDialog from '../../../b2b-common/ConfirmationDialog';
import useB2BTranslation from '../../../b2b-common/hooks/useB2BTranslation';

const Widget = ({ widget, atPublicDashboard = false, password = null, onDelete, onEdit }) => {
    const { t } = useB2BTranslation('b2b-reporting');

    const [widgetData, setWidgetData] = useState(widget);
    const [isHovered, setIsHovered] = useState(false);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

    useEffect(() => {
        if (!isEqual(widget, widgetData)) {
            setWidgetData(widget);
        }
    }, [widget]);

    useEffect(() => {
        const widths_to_check_below = [550, 450, 350, 150];
        const element = document.getElementById(`widget-container-${widget.widgetId}`);
        if (!element) return;

        const updateClasses = (width) => {
            widths_to_check_below.forEach(threshold => {
                if (width < threshold) {
                    element.classList.add(`below-${threshold}-width`);
                } else {
                    element.classList.remove(`below-${threshold}-width`);
                }
            });
        };

        const debouncedUpdateClasses = debounce(updateClasses, 100);

        const resizeObserver = new ResizeObserver(entries => {
            try {
                for (let entry of entries) {
                    debouncedUpdateClasses(entry.contentRect.width);
                }
            } catch (error) {
                console.error('ResizeObserver error:', error);
            }
        });

        // Start observing the element
        resizeObserver.observe(element);

        // Cleanup observer on component unmount
        return () => {
            resizeObserver.unobserve(element);
            debouncedUpdateClasses.cancel();
        };
    }, [widget.widgetId]);

    const handleDeleteWidget = () => {
        onDelete && onDelete(widgetData.widgetId);
        setIsConfirmationOpen(false);
    }

    const widgetStyles = widget.widgetConfig.extraFields.widgetStyles || {};
    const detectHovering = widget.widgetConfig.detectHovering !== undefined ?
        widget.widgetConfig.detectHovering : true;

    const handleMouseEnter = () => {
        detectHovering && setIsHovered(true);
    }

    const handleMouseLeave = () => {
        detectHovering && setIsHovered(false);
    }

    return (
        <div className='widget-container'
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            id={`widget-container-${widget.widgetId}`}
            style={widgetStyles}
        >
            <WidgetFactory widget={widgetData} atPublicDashboard={atPublicDashboard} password={password} />
            {isHovered && (
                <MoreButton
                    icon={<MoreVert />}
                    buttons={[
                        {
                            label: t('Edit'),
                            icon: <Edit />,
                            onClick: () => {
                                onEdit && onEdit(widgetData);
                            }
                        },
                        {
                            label: t('Delete'),
                            icon: <Delete />,
                            onClick: () => {
                                setIsConfirmationOpen(true);
                            }
                        },
                    ]}
                    rolesAllowed={['ADMIN', 'EDITOR']}
                    dropPositionAlign='right'
                    className={'widget-more-button jb01-more-button'}
                />
            )}
            <ConfirmationDialog
                title={t('Delete widget')}
                mainText={t('Are you sure you want to delete the selected widget?')}
                open={isConfirmationOpen}
                onClose={() => setIsConfirmationOpen(false)}
                onConfirm={handleDeleteWidget}
            />
        </div>
    );
};

export default Widget;